import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { getCampaignDetailPath, getCampaignsPath } from "../utils/navigation";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { useCampaign } from "@/features/campaigns/api/useCampaign";
import { useUpdateCampaignMutation } from "@/features/campaigns/api/useUpdateCampaignMutation";
import EditCampaignForm from "@/features/campaigns/components/CampaignForm/EditCampaignForm";
import { handleProblemDetailsError } from "@/lib/helpers/handleProblemDetailsError";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import useToast from "@/lib/hooks/useToast";
import { UpdateCampaignRequest } from "@/types/request/UpdateCampaignRequest";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";
type EditCampaignPageProps = {
  campaignId: string;
  clientId?: string;
};

function EditCampaignPageContent({
  campaignId,
  clientId,
}: EditCampaignPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const campaignsPath = getCampaignsPath(clientId);

  const { data: campaign, isLoading } = useCampaign({
    campaignId,
    clientId,
  });

  const updateCampaign = useUpdateCampaignMutation(clientId);

  const handleSubmit = useCallback(
    (payload: UpdateCampaignRequest) => {
      updateCampaign.mutate(payload, {
        onSuccess: ({ id }) => {
          showToast(t("success.updated", { entity: t("common.campaign") }));
          if (id) {
            const detailPath = getCampaignDetailPath(clientId, id);
            navigate(detailPath);
          } else {
            navigate(-1);
          }
        },
        onError: (error: AxiosError<ValidationErrorResponse | { title?: string; detail?: string; status?: number; field?: string }>) => {
          const response = error.response?.data;
          
          // Handle array format (ValidationErrorResponse from FluentValidation)
          if (Array.isArray(response)) {
            const message = response
              .map(({ errorMessage }) => t(`validation.${errorMessage}`))
              .join(", ");
            showToast(message, "error");
            return;
          }

          // Handle ProblemDetails format (RFC 7807) from handlers
          handleProblemDetailsError({
            error: error as AxiosError<{ title: string; detail: string; status?: number; field?: string }>,
            t,
            showToast,
          });
        },
      });
    },
    [updateCampaign, showToast, t, navigate, clientId]
  );

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.campaigns"), href: campaignsPath },
      {
        label: campaign?.name || t("common.campaign"),
        href: getCampaignDetailPath(clientId, campaignId),
      },
      { label: t("common.edit") },
    ],
  });

  return (
    <ModernPageLayout breadcrumbs={breadcrumbs} className="max-w-lg">
      {isLoading ? (
        <div className="flex h-full w-full items-center justify-center p-6">
          <Loader2 className="size-16 animate-spin text-muted-foreground" />
        </div>
      ) : campaign ? (
        <div className="space-y-8">
          <div className="flex items-center justify-between min-h-12">
            <div>
              <h1 className="text-lg font-semibold text-foreground">
                {campaign.name}
              </h1>
            </div>
          </div>

          <div className="space-y-6">
            <EditCampaignForm
              campaign={campaign}
              campaignId={campaign.id}
              clientId={clientId}
              isSubmitting={updateCampaign.isPending}
              onSubmitForm={handleSubmit}
            />
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          {t("errors.campaignNotFound")}
        </div>
      )}
    </ModernPageLayout>
  );
}

function EditCampaignPage() {
  const { clientId, campaignId } = useParams();
  if (!campaignId) {
    return null;
  }

  return (
    <EditCampaignPageContent clientId={clientId} campaignId={campaignId} />
  );
}
export default EditCampaignPage;
